// « PAGE DE FIN » IN THE ALBUM (coupons plan 2026-10-28, Part 4).
//
// The owner: « "+" add page menu: add "Outro page" (same template as today's
// last notes page). Can be added several times and moved anywhere, and
// deleted. Page count / price / multiple-of-4 rule recalculates. The auto outro
// at the end stays as default. »
//
// THE SHAPE — the smallest one every existing pass already leaves alone:
//
//   { page, layout: '1-up', photos: [], blank: true, kind: 'outro' }
//
// `blank: true` is the editor's own « Page blanche » divider, which every pass
// on BOTH sides (web composer, worker note executors, themes, page checker)
// already treats as structural: never filled with photos, never merged, never
// dropped as an empty page, kept in the array so the page count and the ÷4
// print padding count it. `kind: 'outro'` only changes what is DRAWN on it: the
// brand outro image (app_settings.outro_page_2_url) in the editor, the preview
// and the PDF — instead of a white page. A renderer that does not know `kind`
// prints a white page: never a hole, never a lost photo.
//
// It is a normal entry of pages.pages, so it is billed like any page and the
// ÷4 rule (pdfGenerator roundUpToMultipleOf4) follows by itself.
//
// Created only behind app_settings.editor_outro_page_enabled (default false);
// drawn wherever it exists (a page already in an album keeps printing the same
// if the switch goes off again).

export const OUTRO_KIND = 'outro' as const;

export interface OutroPage {
  page: number;
  layout: '1-up';
  photos: string[];
  blank: true;
  kind: typeof OUTRO_KIND;
}

interface Numbered { page: number }

/** Is this album page a « Page de fin » (the brand outro image)? */
export function isOutroPage(p: unknown): boolean {
  return !!p && typeof p === 'object' && (p as { kind?: unknown }).kind === OUTRO_KIND;
}

/** A fresh « Page de fin » numbered `page`. */
export function makeOutroPage(page: number): OutroPage {
  return { page, layout: '1-up', photos: [], blank: true, kind: OUTRO_KIND };
}

export function countOutroPages(pages: ReadonlyArray<unknown>): number {
  let n = 0;
  for (const p of pages) if (isOutroPage(p)) n++;
  return n;
}

/** Old page number → new page number, for every page that survives. */
export type PageMap = Map<number, number>;

function renumber<T extends Numbered>(pages: readonly T[]): { pages: T[]; pageMap: PageMap } {
  const pageMap: PageMap = new Map();
  const out = pages.map((p, i) => {
    pageMap.set(p.page, i + 1);
    return p.page === i + 1 ? p : { ...p, page: i + 1 };
  });
  return { pages: out, pageMap };
}

/**
 * Insert ONE « Page de fin » after array index `afterArrayIndex` (-1 = before
 * the first page) and renumber 1…N. `pageMap` maps the old numbers of the
 * existing pages to their new ones (the new page is not in it).
 */
export function insertOutroPage<T extends Numbered>(
  pages: readonly T[],
  afterArrayIndex: number,
): { pages: Array<T | OutroPage>; pageMap: PageMap; insertedIndex: number } {
  const at = Math.max(0, Math.min(pages.length, afterArrayIndex + 1));
  const pageMap: PageMap = new Map();
  const out: Array<T | OutroPage> = [];
  pages.forEach((p, i) => {
    if (i === at) out.push(makeOutroPage(out.length + 1));
    const n = out.length + 1;
    pageMap.set(p.page, n);
    out.push(p.page === n ? p : { ...p, page: n });
  });
  if (at >= pages.length) out.push(makeOutroPage(out.length + 1));
  return { pages: out, pageMap, insertedIndex: at };
}

/**
 * « ← Déplacer / Déplacer → » — move ONE page by one place (swap with its
 * neighbour) and renumber. Null when refused: unknown page, the edge of the
 * album, or (`pinnedFirst`, a themed book) a move onto / off array index 0 —
 * the themed S1 opening is print page 1 and never moves (editorSpreads).
 */
export function movePageBy<T extends Numbered>(
  pages: readonly T[],
  pageNumber: number,
  dir: -1 | 1,
  pinnedFirst = false,
): { pages: T[]; pageMap: PageMap; newIndex: number } | null {
  const i = pages.findIndex((p) => p.page === pageNumber);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= pages.length) return null;
  if (pinnedFirst && (i === 0 || j === 0)) return null;
  const next = [...pages];
  [next[i], next[j]] = [next[j], next[i]];
  const { pages: out, pageMap } = renumber(next);
  return { pages: out, pageMap, newIndex: j };
}

/** Where each « Page de fin » stood, by array index (ascending). */
export interface OutroSlot<T> { index: number; page: T }

/** The album without its « Pages de fin » (renumbered 1…N), and where they were. */
export function splitOutroPages<T extends Numbered>(pages: readonly T[]): { content: T[]; outros: Array<OutroSlot<T>> } {
  const outros: Array<OutroSlot<T>> = [];
  const content: T[] = [];
  pages.forEach((p, index) => {
    if (isOutroPage(p)) outros.push({ index, page: p });
    else content.push(p);
  });
  return { content: renumber(content).pages, outros };
}

/**
 * « Régénérer » rebuilds the photo pages; the « Pages de fin » the customer
 * placed stay where they were (same array index, clamped to the new length).
 * Any outro page the fresh layout itself carries is dropped first — never two.
 * `pageMap` maps the FRESH layout's page numbers to the final ones, so the
 * caller can re-key its `p{page}-s{slot}` framings.
 */
export function reinsertOutroPages<T extends Numbered>(
  fresh: readonly T[],
  outros: ReadonlyArray<OutroSlot<T>>,
): { pages: T[]; pageMap: PageMap } {
  const base = fresh.filter((p) => !isOutroPage(p));
  const slots = [...outros].sort((a, b) => a.index - b.index);
  const out: T[] = [];
  const origin: Array<number | null> = [];   // fresh page number, null = outro
  let k = 0;
  for (const p of base) {
    while (k < slots.length && slots[k].index <= out.length) {
      out.push(slots[k].page); origin.push(null); k++;
    }
    out.push(p); origin.push(p.page);
  }
  for (; k < slots.length; k++) { out.push(slots[k].page); origin.push(null); }
  const pageMap: PageMap = new Map();
  const pages = out.map((p, i) => {
    const from = origin[i];
    if (from != null) pageMap.set(from, i + 1);
    return p.page === i + 1 ? p : { ...p, page: i + 1 };
  });
  return { pages, pageMap };
}

/**
 * Re-key a `p{page}-s{slot}` framing map after pages were renumbered. Keys of
 * pages the map does not know are dropped (their page is gone).
 */
export function remapPageKeyed<V>(positions: Readonly<Record<string, V>>, pageMap: PageMap): Record<string, V> {
  const out: Record<string, V> = {};
  for (const [key, v] of Object.entries(positions)) {
    const m = /^p(\d+)-(.*)$/.exec(key);
    if (!m) { out[key] = v; continue; }
    const to = pageMap.get(Number(m[1]));
    if (to == null) continue;
    out[`p${to}-${m[2]}`] = v;
  }
  return out;
}
